/**
 * Elasticity of demand: the midpoint (arc) formula, point elasticity along a straight
 * line, and the verdicts that go with each measure.
 *
 * The midpoint formula is the one OpenStax Principles of Economics and Mankiw teach:
 * each percentage change is the change divided by the AVERAGE of the start and end
 * values, so the answer is the same whichever way the move is read (up from A to B, or
 * back down from B to A). Percentages are kept as fractions (0.25 = 25%).
 */

/** Anything within this of a boundary (1 for unit elastic, 0 for zero) counts as on it. */
const TOLERANCE = 1e-9;

export interface ArcResult {
  /** Change in quantity, Q₂ − Q₁. */
  changeQ: number;
  /** Change in the driver (price or income), X₂ − X₁. */
  changeX: number;
  /** (Q₁ + Q₂) ÷ 2 */
  meanQ: number;
  /** (X₁ + X₂) ÷ 2 */
  meanX: number;
  /** Midpoint percentage change in quantity, as a fraction. */
  percentQ: number;
  /** Midpoint percentage change in the driver, as a fraction. */
  percentX: number;
  /** %ΔQ ÷ %ΔX, signed. */
  elasticity: number;
}

export type ArcFailure = "same-driver" | "empty-quantity" | "empty-driver";

/**
 * The midpoint elasticity, or the reason there is none: the driver did not move (a
 * zero percentage change in the denominator), or a pair of values averages to zero.
 */
export function arcElasticity(
  q1: number,
  q2: number,
  x1: number,
  x2: number
): ArcResult | ArcFailure {
  const meanQ = (q1 + q2) / 2;
  const meanX = (x1 + x2) / 2;
  if (meanQ === 0) {
    return "empty-quantity";
  }
  if (meanX === 0) {
    return "empty-driver";
  }
  if (x1 === x2) {
    return "same-driver";
  }
  const changeQ = q2 - q1;
  const changeX = x2 - x1;
  const percentQ = changeQ / meanQ;
  const percentX = changeX / meanX;
  return {
    changeQ,
    changeX,
    meanQ,
    meanX,
    percentQ,
    percentX,
    elasticity: percentQ / percentX,
  };
}

/** Point elasticity (dQ/dX)·(X/Q) at (x, q) for a line whose slope dQ/dX is `slope`. */
export function pointElasticity(
  slope: number,
  x: number,
  q: number
): number | null {
  if (q === 0) {
    return null;
  }
  return (slope * x) / q;
}

export type PriceVerdict =
  | "perfectlyInelastic"
  | "inelastic"
  | "unitElastic"
  | "elastic";

/** The price-elasticity verdict from |E|. */
export function priceVerdict(elasticity: number): PriceVerdict {
  const size = Math.abs(elasticity);
  if (size < TOLERANCE) {
    return "perfectlyInelastic";
  }
  if (Math.abs(size - 1) < TOLERANCE) {
    return "unitElastic";
  }
  return size < 1 ? "inelastic" : "elastic";
}

export type IncomeVerdict = "inferior" | "necessity" | "luxury" | "unitIncome";

/**
 * Income elasticity: negative is an inferior good; between 0 and 1 a normal necessity;
 * above 1 a normal luxury. Exactly 0 is reported as a necessity edge case by the
 * caller, so it is folded into "necessity" here.
 */
export function incomeVerdict(elasticity: number): IncomeVerdict {
  if (elasticity < -TOLERANCE) {
    return "inferior";
  }
  if (Math.abs(elasticity - 1) < TOLERANCE) {
    return "unitIncome";
  }
  return elasticity > 1 ? "luxury" : "necessity";
}

export type CrossVerdict = "substitutes" | "complements" | "unrelated";

/** Cross-price elasticity: positive for substitutes, negative for complements. */
export function crossVerdict(elasticity: number): CrossVerdict {
  if (Math.abs(elasticity) < TOLERANCE) {
    return "unrelated";
  }
  return elasticity > 0 ? "substitutes" : "complements";
}

export type RevenueDirection = "up" | "down" | "same";

export interface RevenueEffect {
  before: number;
  after: number;
  change: number;
  direction: RevenueDirection;
}

/** Total revenue P·Q before and after, and which way it moved. */
export function revenueEffect(
  p1: number,
  q1: number,
  p2: number,
  q2: number
): RevenueEffect {
  const before = p1 * q1;
  const after = p2 * q2;
  const change = after - before;
  let direction: RevenueDirection = "same";
  if (Math.abs(change) > TOLERANCE * Math.max(1, Math.abs(before))) {
    direction = change > 0 ? "up" : "down";
  }
  return { before, after, change, direction };
}
